/**
 * Elektron xizmatlar — integratsiya shlyuzi (ЧТЗ 4.2.9, 4.2.11.5).
 *
 * Kartochkada: xizmat nomi, qisqa vazifasi, qanday ishlashi va aniq tugmalar
 * («Ruxsatnoma olish», «Maydonlarni ko'rish», «Auksionga o'tish», «Ijara
 * hisobini ko'rish»…) hamda «Batafsil» — `/xizmatlar/<kod>` sahifasi.
 *
 * Tashqi/ichki xizmatga o'tish `GET /portal/public/services/<kod>/redirect/`
 * orqali: kirmagan foydalanuvchi OneID ga, so'ng `/xizmat/<kod>` orqali
 * xizmatga qaytadi. Xaritada tanlangan uchastka (`context`) faqat integratsiyasi
 * kelishilgan tizimga uzatiladi; aks holda foydalanuvchiga uchastka raqami
 * ko'rsatilib, tashqi tizimda qanday davom etishi tushuntiriladi.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "@/shared/api/client";
import { portalApi } from "@/shared/api/endpoints";
import type { ServiceContext } from "@/shared/api/types";
import { SERVICE_CATALOG, serviceMapHref } from "@/shared/data/serviceCatalog";
import type { CatalogService, ServiceAction } from "@/shared/data/serviceCatalog";
import { Icon } from "@/shared/ui/Icon";
import { Modal } from "@/shared/ui/Modal";

/** Xizmat manziliga o'tish: ichki marshrut — SPA ichida, tashqi — to'liq sahifa. */
export function openServiceUrl(url: string, navigate: (to: string) => void) {
  if (/^https?:\/\//i.test(url)) {
    window.location.assign(url);
  } else {
    navigate(url.startsWith("/") ? url : `/${url}`);
  }
}

/** OneID dan qaytish manzili — uchastka konteksti yo'qolmasligi uchun so'rovda saqlanadi. */
export function gatewayReturnPath(code: string, context: ServiceContext = {}): string {
  const params = new URLSearchParams(
    Object.entries(context).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
  const query = params.toString();
  return `/xizmat/${encodeURIComponent(code)}${query ? `?${query}` : ""}`;
}

interface PlotNotice {
  url: string;
  plot: string;
  serviceTitle: string;
}

/**
 * Shlyuz orqali xizmatga o'tish. Uchastka raqami tashqi tizimga uzatilmasa
 * (`context: not_supported`) — o'tishdan oldin raqam va yo'riqnoma ko'rsatiladi.
 */
export function useServiceLauncher() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<PlotNotice | null>(null);
  const [copied, setCopied] = useState(false);

  async function launch(code: string, serviceTitle: string, context: ServiceContext = {}) {
    setError("");
    setBusy(code);
    try {
      const next = context.plot ? gatewayReturnPath(code, context) : "";
      const result = await portalApi.serviceRedirect(code, next, context);
      if (!result.requires_login && result.context === "not_supported" && context.plot) {
        setNotice({ url: result.url, plot: context.plot, serviceTitle });
        return;
      }
      openServiceUrl(result.url, navigate);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.isOffline
          ? "Server bilan aloqa yo'q — xizmatga o'tib bo'lmadi. Keyinroq urinib ko'ring."
          : caught instanceof ApiError
            ? caught.message
            : "Xizmatga o'tib bo'lmadi.",
      );
    } finally {
      setBusy("");
    }
  }

  async function copyPlot() {
    if (!notice) return;
    try {
      await navigator.clipboard.writeText(notice.plot);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const dialog = (
    <Modal
      open={Boolean(notice)}
      title="Uchastka ma'lumotini eslab qoling"
      onClose={() => setNotice(null)}
      size="sm"
      footer={
        notice ? (
          <>
            <button type="button" className="btn btn-outline" onClick={() => setNotice(null)}>
              Bekor qilish
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => openServiceUrl(notice.url, navigate)}
            >
              <Icon name="external" size={16} />
              Xizmatga o'tish
            </button>
          </>
        ) : null
      }
    >
      {notice ? (
        <div className="plot-notice">
          <div className="plot-notice-id">
            <span>Uchastka / maydon</span>
            <b className="num">{notice.plot}</b>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copyPlot()}>
              <Icon name={copied ? "check" : "file"} size={14} />
              {copied ? "Nusxalandi" : "Nusxa olish"}
            </button>
          </div>
          <p>
            «{notice.serviceTitle}» tizimi hozircha uchastka ma'lumotlarini portaldan avtomatik qabul
            qilmaydi. Xizmat sahifasida arizani to'ldirishda shu raqam yoki nomni kiriting yoki maydonni
            o'sha tizim xaritasida tanlang.
          </p>
        </div>
      ) : null}
    </Modal>
  );

  return { launch, busy, error, dialog };
}

/** Xizmat amali tugmasi (shlyuz, xarita yoki ichki havola). */
export function ServiceActionButton({
  service,
  action,
  primary,
  launcher,
  purpose = "",
}: {
  service: CatalogService;
  action: ServiceAction;
  primary: boolean;
  launcher: ReturnType<typeof useServiceLauncher>;
  purpose?: string;
}) {
  const className = `btn ${primary ? "btn-primary" : "btn-outline"} btn-sm`;
  const body = (
    <>
      <Icon name={action.icon} size={16} />
      <span>{action.label}</span>
    </>
  );
  if (action.kind === "map") {
    return (
      <Link className={className} to={serviceMapHref(service, purpose)}>
        {body}
      </Link>
    );
  }
  if (action.kind === "link") {
    return (
      <Link className={className} to={action.target ?? "/"}>
        {body}
      </Link>
    );
  }
  const code = action.target ?? service.code;
  return (
    <button
      type="button"
      className={className}
      onClick={() => void launcher.launch(code, service.provider.system, purpose ? { purpose } : {})}
      disabled={Boolean(launcher.busy)}
      aria-busy={launcher.busy === code}
    >
      {launcher.busy === code ? <span className="gateway-spinner" aria-hidden="true" /> : <Icon name={action.icon} size={16} />}
      <span>{action.label}</span>
    </button>
  );
}

/** Xizmat kartochkasi: belgi, nom, qisqa izoh, asosiy tugma va «Batafsil» (qolgani — xizmat sahifasida). */
function ServiceCard({
  service,
  launcher,
}: {
  service: CatalogService;
  launcher: ReturnType<typeof useServiceLauncher>;
}) {
  const main = service.actions.find((action) => action.kind !== "map") ?? service.actions[0];
  return (
    <article className="svc-card">
      <span className="svc-icon" aria-hidden="true">
        <Icon name={service.icon} size={26} />
      </span>
      <h4>{service.title}</h4>
      <p className="svc-summary">{service.summary}</p>
      <div className="svc-actions">
        {main ? (
          <ServiceActionButton service={service} action={main} primary launcher={launcher} />
        ) : null}
        <Link className="btn btn-outline btn-sm" to={`/xizmatlar/${service.code}`}>
          Batafsil
        </Link>
      </div>
    </article>
  );
}

export function ServiceGateway() {
  const launcher = useServiceLauncher();

  const groups: Array<{ title: string; items: CatalogService[] }> = [
    { title: "Davlat xizmatlari", items: SERVICE_CATALOG.filter((item) => item.group === "state") },
    { title: "Platforma xizmatlari", items: SERVICE_CATALOG.filter((item) => item.group === "platform") },
  ];

  return (
    <div className="gateway" data-reveal>
      {launcher.error ? (
        <p className="alert alert-error" role="alert">
          <Icon name="alert" size={18} />
          <span>{launcher.error}</span>
        </p>
      ) : null}

      {groups.map((group) =>
        group.items.length ? (
          <section className="gateway-group" key={group.title} aria-label={group.title}>
            <h3 className="svc-group-title">{group.title}</h3>
            <div className="svc-grid">
              {group.items.map((service) => (
                <ServiceCard key={service.code} service={service} launcher={launcher} />
              ))}
            </div>
          </section>
        ) : null,
      )}

      {launcher.dialog}
    </div>
  );
}
